import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import RichEditor from "../components/RichEditor";
import { StatusBadge, PriorityBadge, TypeBadge } from "../components/Badges";
import { ChevronLeft, Pencil } from "lucide-react";
import { STATUSES, PRIORITIES, TYPES, EFFORTS, IMPACTS, PHASES } from "../constants/enums";
import AttachmentGallery, { useAttachmentPaste } from "../components/dev-tracker/AttachmentGallery";
import VideoLinks from "../components/dev-tracker/VideoLinks";
import MenuAreaSelect from "../components/dev-tracker/MenuAreaSelect";
import AssigneeSelect from "../components/dev-tracker/AssigneeSelect";
import VoiceRecorder from "../components/dev-tracker/VoiceRecorder";
import AICleanupCard from "../components/dev-tracker/AICleanupCard";

export default function ItemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = user?.role === "admin";
  const isCreator = item && user && item.created_by === user.id;
  const isAssignee = item && user && item.assigned_to === user.id;
  const canEdit = isAdmin || isCreator || isAssignee;
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", type: "Bug" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [activity, setActivity] = useState([]);
  // Attachment upload (paste / drag-drop) — see useAttachmentPaste below
  const [uploadingCount, setUploadingCount] = useState(0);

  const uploadImage = async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setUploadingCount((n) => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: att } = await api.post(`/items/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItem((it) => ({ ...it, attachments: [...(it.attachments || []), att] }));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingCount((n) => Math.max(0, n - 1));
    }
  };
  const pasteProps = useAttachmentPaste(uploadImage);

  const loadAll = async () => {
    const [itemRes, usersRes, commentsRes, actsRes] = await Promise.all([
      api.get(`/items/${id}`),
      api.get("/users"),
      api.get(`/items/${id}/comments`),
      api.get(`/items/${id}/activity`),
    ]);
    setItem(itemRes.data);
    setUsers(usersRes.data);
    setComments(commentsRes.data);
    setActivity(actsRes.data);
  };

  useEffect(() => {
    loadAll().catch((e) => setError(formatApiError(e.response?.data?.detail) || "Failed to load"));
  }, [id]);

  useEffect(() => { api.get("/dev/menu-areas").then(({ data }) => setAreas(data)).catch(() => {}); }, []);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const patch = async (changes) => {
    try {
      const { data } = await api.patch(`/items/${id}`, changes);
      setItem(data);
      try {
        const { data: acts } = await api.get(`/items/${id}/activity`);
        setActivity(acts);
      } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("Failed to refresh activity after patch:", err); }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const postComment = async () => {
    if (!newComment.trim() || newComment === "<br>") return;
    if (posting) return;  // guard double-submit
    setPosting(true);
    const idempotency_key = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    try {
      const { data } = await api.post(`/items/${id}/comments`, { body: newComment, idempotency_key });
      setComments((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, data]));
      setNewComment("");
      try {
        const { data: acts } = await api.get(`/items/${id}/activity`);
        setActivity(acts);
      } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("Failed to refresh activity after comment:", err); }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to post");
    } finally { setPosting(false); }
  };

  const openEdit = () => {
    setEditForm({ title: item.title, description: item.description || "", type: item.type });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.title.trim()) { setError("Title is required."); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/items/${id}`, {
        title: editForm.title,
        description: editForm.description,
        type: editForm.type,
      });
      setItem(data);
      // refresh activity so the change appears immediately in the timeline
      try {
        const { data: acts } = await api.get(`/items/${id}/activity`);
        setActivity(acts);
      } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("Failed to refresh activity after save:", err); }
      setEditOpen(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingEdit(false); }
  };

  if (!item) {
    return <Layout><div className="text-slate-400">{error || "Loading..."}</div></Layout>;
  }

  return (
    <Layout>
      <Link to="/items/all" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-orange-400 mb-4" data-testid="back-to-items">
        <ChevronLeft className="w-4 h-4" /> Back to items
      </Link>

      <div
        {...pasteProps}
        className={`grid grid-cols-1 lg:grid-cols-3 gap-6 relative rounded-xl transition ${pasteProps._dragActive ? "ring-2 ring-orange-400/60 ring-offset-2 ring-offset-slate-950" : ""}`}
        data-testid="item-detail-dropzone"
      >
        {pasteProps._dragActive && (
          <div className="absolute inset-0 z-40 pointer-events-none grid place-items-center bg-orange-500/[0.06] rounded-xl">
            <div className="text-orange-200 text-sm font-mono tracking-widest uppercase bg-slate-900/80 border border-orange-400/40 px-4 py-2 rounded-full">
              Drop image to attach
            </div>
          </div>
        )}
        {uploadingCount > 0 && (
          <div className="absolute top-2 right-2 z-40 text-[10px] font-mono uppercase tracking-widest text-orange-200 bg-orange-500/10 border border-orange-400/40 px-2 py-1 rounded-full" data-testid="attachment-uploading">
            Uploading {uploadingCount}…
          </div>
        )}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-dark p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <TypeBadge value={item.type} />
                <StatusBadge value={item.status} />
                <PriorityBadge value={item.priority} />
              </div>
              {canEdit && (
                <button onClick={openEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-white/10 hover:bg-white/5" data-testid="edit-item-button">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              )}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="item-detail-title">{item.title}</h1>
            <div className="text-xs text-slate-500 mt-2">
              Created {new Date(item.created_at).toLocaleString()} · Updated {new Date(item.updated_at).toLocaleString()}
            </div>
            <div className="mt-5 rich-content" data-testid="item-description" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description || "<em class='text-slate-500'>No description provided.</em>") }} />
          </div>

          <div className="card-dark p-6">
            <AttachmentGallery
              itemId={item.id}
              attachments={item.attachments || []}
              onChange={(atts) => setItem((it) => ({ ...it, attachments: atts }))}
            />
          </div>

          <div className="card-dark p-6">
            <VideoLinks
              itemId={item.id}
              links={item.video_links || []}
              onChange={(links) => setItem((it) => ({ ...it, video_links: links }))}
            />
          </div>

          <AICleanupCard
            item={item}
            areas={areas}
            onUpdated={(payload) => setItem((it) => ({ ...it, ai_cleanup: payload }))}
            onApply={(p) => patch(p)}
          />

          <div className="card-dark p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Conversation</h2>
              <VoiceRecorder
                itemId={item.id}
                onTranscribed={(comment) => setComments((prev) => [...prev, comment])}
              />
            </div>
            {comments.length === 0 && <div className="text-slate-500 text-sm mb-4" data-testid="comments-empty">No notes yet. Start the conversation.</div>}
            <div className="space-y-4 mb-6">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className={`w-9 h-9 rounded-full grid place-items-center font-display font-bold text-sm shrink-0 ${
                    c.kind === "voice"
                      ? "bg-gradient-to-br from-rose-500 to-orange-500"
                      : "bg-gradient-to-br from-indigo-500 to-purple-600"
                  }`}>
                    {c.kind === "voice"
                      ? <span title="Voice note" className="text-white text-base">🎙</span>
                      : (usersById[c.author_id]?.name?.[0]?.toUpperCase() || "?")}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{c.author_name || usersById[c.author_id]?.name || "Unknown"}</span>
                      <span className="text-slate-500 text-xs">{new Date(c.created_at).toLocaleString()}</span>
                      {c.kind === "voice" && (
                        <span className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-rose-400/40 bg-rose-500/10 text-rose-200">
                          voice{c.duration_s ? ` · ${Math.round(c.duration_s)}s` : ""}
                        </span>
                      )}
                    </div>
                    <div className="rich-content mt-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body) }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/5 pt-5">
              <label className="text-xs uppercase tracking-wider text-slate-300 block mb-2">Add a note</label>
              <RichEditor value={newComment} onChange={setNewComment} placeholder="Add a comment or paste a screenshot..." testId="comment-editor" />
              <div className="mt-3 flex justify-end">
                <button className="btn-coral" onClick={postComment} disabled={posting} data-testid="comment-submit">
                  {posting ? "Posting..." : "Post note"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="card-dark p-5">
            <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-4">Details</h3>
            <DetailField label="Status" testId="detail-status">
              <select value={item.status} onChange={(e) => patch({ status: e.target.value })} className="input-dark py-1.5 text-sm" data-testid="detail-status-select">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </DetailField>
            <DetailField label="Priority" testId="detail-priority">
              <select value={item.priority} onChange={(e) => patch({ priority: e.target.value })} className="input-dark py-1.5 text-sm" data-testid="detail-priority-select">
                {PRIORITIES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </DetailField>
            <DetailField label="System" testId="detail-system">
              <select
                value={item.talmia_version}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === item.talmia_version) return;
                  // Switching system invalidates the (system-scoped) menu area.
                  patch({ talmia_version: v, menu_area: null, menu_area_id: null });
                }}
                className="input-dark py-1.5 text-sm"
                data-testid="detail-system-select"
              >
                <option value="1">Talmia 1</option>
                <option value="2">Talmia 2</option>
                <option value="both">Both</option>
              </select>
            </DetailField>
            <DetailField label="Type" testId="detail-type">
              <select value={item.type} onChange={(e) => patch({ type: e.target.value })} className="input-dark py-1.5 text-sm" data-testid="detail-type-select">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </DetailField>
            <DetailField label="Menu area" testId="detail-menu-area">
              <MenuAreaSelect
                value={item.menu_area_id}
                valueLabel={item.menu_area}
                compact
                system={item.talmia_version === "both" ? null : item.talmia_version}
                onChange={(area) => patch(area
                  ? { menu_area: area.label, menu_area_id: area.id }
                  : { menu_area: null, menu_area_id: null })}
              />
            </DetailField>
            <DetailField label="Assigned to" testId="detail-assignee">
              <AssigneeSelect
                value={item.assigned_to}
                users={users}
                onChange={(v) => patch({ assigned_to: v })}
                testid="detail-assignee-select"
              />
            </DetailField>
            <DetailField label="Created by" testId="detail-creator">
              {isAdmin ? (
                <select
                  value={item.created_by || ""}
                  onChange={(e) => patch({ created_by: e.target.value })}
                  className="input-dark py-1.5 text-sm"
                  data-testid="detail-creator-select"
                >
                  {!usersById[item.created_by] && item.created_by && (
                    <option value={item.created_by}>Unknown user</option>
                  )}
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <span className="text-sm">{usersById[item.created_by]?.name || "—"}</span>
              )}
            </DetailField>
            {!isAdmin && (
              <p className="text-[11px] text-slate-500 mt-3">Anyone can update status, type, priority, system and assignment. Only the “Created by” field is admin-only.</p>
            )}
          </div>

          <ScoringPanel item={item} onPatch={patch} />
          {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}

          <div className="card-dark p-5">
            <ActivityTimeline activity={activity} testId="activity-timeline" />
          </div>
        </aside>
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="edit-item-modal" onClick={() => setEditOpen(false)}>
          <div className="card-dark w-full max-w-3xl p-6 fade-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold mb-4">Edit item</h3>
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-300">Title *</label>
                <input
                  className="input-dark mt-1.5" required maxLength={200}
                  value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-item-title-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-300">Type</label>
                <select className="input-dark mt-1.5" value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} data-testid="edit-item-type-select">
                  <option>Bug</option>
                  <option>Feature</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-300 block mb-1.5">Description</label>
                <RichEditor key={item.id} value={editForm.description} onChange={(v) => setEditForm({ ...editForm, description: v })} placeholder="Describe the bug or feature..." testId="edit-item-description" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditOpen(false)} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5">Cancel</button>
                <button type="submit" disabled={savingEdit} className="btn-coral" data-testid="edit-item-save">{savingEdit ? "Saving..." : "Save changes"}</button>
              </div>
            </form>

            <div className="mt-8 border-t border-white/5 pt-5">
              <ActivityTimeline activity={activity} testId="activity-timeline-modal" />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Scoring block (Wave 2) — Function / Effort / Impact / ROI / Phase / Notes.
// All optional, autosave on change (selects) or blur (text/number).
function ScoringPanel({ item, onPatch }) {
  const [fn, setFn] = useState(item.function || "");
  const [roi, setRoi] = useState(item.roi ?? "");
  const [notes, setNotes] = useState(item.notes || "");

  useEffect(() => { setFn(item.function || ""); setRoi(item.roi ?? ""); setNotes(item.notes || ""); }, [item.id]);

  const saveFn    = () => { if ((fn || "") !== (item.function || "")) onPatch({ function: fn || null }); };
  const saveNotes = () => { if ((notes || "") !== (item.notes || "")) onPatch({ notes: notes || null }); };
  const saveRoi   = () => {
    const cur = item.roi ?? "";
    if (String(roi) !== String(cur)) onPatch({ roi: String(roi).trim() === "" ? null : Number(roi) });
  };

  return (
    <div className="card-dark p-5" data-testid="scoring-panel">
      <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-4">Scoring</h3>

      <div className="mb-3">
        <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Function</label>
        <input
          type="text" value={fn} onChange={(e) => setFn(e.target.value)} onBlur={saveFn}
          placeholder="Specific feature / function"
          className="input-dark py-1.5 text-sm w-full" data-testid="scoring-function"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Effort</label>
          <select value={item.effort || ""} onChange={(e) => onPatch({ effort: e.target.value || null })} className="input-dark py-1.5 text-sm w-full" data-testid="scoring-effort">
            <option value="">—</option>
            {EFFORTS.map((e) => <option key={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Impact</label>
          <select value={item.impact ?? ""} onChange={(e) => onPatch({ impact: e.target.value === "" ? null : Number(e.target.value) })} className="input-dark py-1.5 text-sm w-full" data-testid="scoring-impact">
            <option value="">—</option>
            {IMPACTS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">ROI</label>
          <input type="number" step="0.1" value={roi} onChange={(e) => setRoi(e.target.value)} onBlur={saveRoi} className="input-dark py-1.5 text-sm w-full" data-testid="scoring-roi" title="value for effort — higher = do sooner" />
        </div>
      </div>

      <div className="mb-3">
        <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Phase</label>
        <select value={item.phase || ""} onChange={(e) => onPatch({ phase: e.target.value || null })} className="input-dark py-1.5 text-sm w-full" data-testid="scoring-phase">
          <option value="">—</option>
          {PHASES.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Notes</label>
        <textarea
          rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes}
          placeholder="Use for Pts, dependencies, what it retires, and any extra detail."
          className="input-dark py-1.5 text-sm w-full resize-y" data-testid="scoring-notes"
        />
      </div>
    </div>
  );
}


function DetailField({ label, children, testId }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-b-0 gap-3" data-testid={testId}>
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-right">{children}</div>
    </div>
  );
}

function ActivityTimeline({ activity, testId = "activity-timeline" }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display text-sm font-bold uppercase tracking-wider text-slate-300">Activity timeline</h4>
        <span className="text-[11px] text-slate-500">{activity.length} {activity.length === 1 ? "event" : "events"}</span>
      </div>
      {activity.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="activity-empty">No activity yet.</p>
      ) : (
        <ul className="relative space-y-3">
          {activity.map((a) => (
            <li key={a.id} className="relative pl-5" data-testid={`activity-${a.id}`}>
              <span className="absolute left-0 top-2 w-2 h-2 rounded-full bg-orange-400 ring-2 ring-orange-400/20" />
              <div className="text-xs text-slate-400">
                <b className="text-slate-200">{a.actor_name || "Someone"}</b>
                {" "}
                {a.kind === "created" && <span>{a.summary || "created the item"}</span>}
                {a.kind === "commented" && <span>added a note</span>}
                {a.kind === "updated" && a.changes.length > 0 && (
                  <span>
                    updated{" "}
                    {a.changes.map((c, idx) => (
                      <span key={`${a.id}-${c.field}-${idx}`}>
                        {idx > 0 && ", "}
                        <span className="text-slate-300">{c.field.replace(/_/g, " ")}</span>
                        {" "}
                        {c.field === "description"
                          ? <span className="text-slate-500">(content)</span>
                          : <>
                              <span className="text-slate-500">{String(c.from)}</span>
                              <span className="text-slate-600"> → </span>
                              <span className="text-orange-300">{String(c.to)}</span>
                            </>
                        }
                      </span>
                    ))}
                  </span>
                )}
                <span className="text-slate-600"> · {new Date(a.created_at).toLocaleString()}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
